import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AuthoringAdminService from "../../../apis/AuthoringAdminService";
import {
  extractColumnsFromRows,
  formatKeyToLabel,
  formatValueForDetails,
  formatValueForTable,
  normalizeApiListResponse,
  sanitizeRowsForDisplay,
} from "../shared/tableUtils";

const DEFAULT_ROWS_PER_PAGE = 10;

const buildYearOptions = (defaultYear) => {
  const startYear = defaultYear - 3;
  const endYear = defaultYear + 3;
  const years = [];

  for (let year = startYear; year <= endYear; year += 1) {
    years.push(year);
  }

  return years;
};

const EnrollmentsManager = ({ defaultYear = 2026 }) => {
  const [year, setYear] = useState(defaultYear);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const pageTokensRef = useRef({ 0: null });

  const yearOptions = useMemo(() => buildYearOptions(defaultYear), [defaultYear]);

  const columns = useMemo(() => extractColumnsFromRows(rows), [rows]);

  const loadEnrollments = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const currentTokens = pageTokensRef.current || {};
      const tokenForPage = Object.prototype.hasOwnProperty.call(
        currentTokens,
        page
      )
        ? currentTokens[page]
        : null;

      const response = await AuthoringAdminService.fetchEnrollments({
        page,
        pageSize: rowsPerPage,
        year,
        nextToken: tokenForPage,
      });

      const {
        items,
        total,
        page: responsePage,
        pageSize: responsePageSize,
        nextToken,
        hasMore: responseHasMore,
      } = normalizeApiListResponse(response);

      const itemsArray = Array.isArray(items) ? items : [];
      const sanitizedRows = sanitizeRowsForDisplay(itemsArray);

      setRows(sanitizedRows);

      let normalizedNextToken = null;

      if (nextToken !== undefined && nextToken !== null) {
        if (typeof nextToken === "string") {
          const trimmedToken = nextToken.trim();
          if (trimmedToken !== "") {
            normalizedNextToken = trimmedToken;
          }
        } else {
          normalizedNextToken = nextToken;
        }
      }

      const hasNextPage = Boolean(responseHasMore) || Boolean(normalizedNextToken);
      setHasMoreResults(hasNextPage);

      const currentItemsCount = itemsArray.length;
      const minimumKnownTotal = page * rowsPerPage + currentItemsCount;

      let resolvedTotal =
        typeof total === "number" && Number.isFinite(total)
          ? total
          : minimumKnownTotal;

      if (resolvedTotal < minimumKnownTotal) {
        resolvedTotal = minimumKnownTotal;
      }

      setTotalCount(resolvedTotal);

      if (typeof responsePage === "number" && Number.isFinite(responsePage)) {
        const normalizedPage = Math.max(responsePage - 1, 0);
        if (normalizedPage !== page) {
          setPage(normalizedPage);
        }
      }

      if (
        typeof responsePageSize === "number" &&
        Number.isFinite(responsePageSize) &&
        responsePageSize > 0 &&
        responsePageSize !== rowsPerPage
      ) {
        setRowsPerPage(responsePageSize);
      }

      const updatedTokens = { ...currentTokens };

      if (page === 0) {
        updatedTokens[0] = null;
      }

      if (hasNextPage && normalizedNextToken) {
        updatedTokens[page + 1] = normalizedNextToken;
      } else {
        delete updatedTokens[page + 1];
      }

      Object.keys(updatedTokens).forEach((key) => {
        const numericKey = Number(key);
        if (!Number.isFinite(numericKey)) {
          return;
        }
        if (numericKey > page + 1) {
          delete updatedTokens[numericKey];
        }
      });

      pageTokensRef.current = updatedTokens;
    } catch (requestError) {
      console.error("Failed to load enrollments", requestError);
      const message =
        requestError?.response?.data?.message ||
        requestError?.message ||
        "Unable to load enrollments right now.";
      setError(message);
      setRows([]);
      setTotalCount(0);
      setHasMoreResults(false);
      pageTokensRef.current = { 0: null };
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, year]);

  useEffect(() => {
    void loadEnrollments();
  }, [loadEnrollments]);

  const handleChangePage = (_, newPage) => {
    if (newPage === page) {
      return;
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = Number(event.target.value);

    if (!Number.isFinite(newSize) || newSize <= 0) {
      return;
    }

    pageTokensRef.current = { 0: null };
    setHasMoreResults(false);
    setRowsPerPage(newSize);
    setPage(0);
  };

  const handleResetYear = () => {
    pageTokensRef.current = { 0: null };
    setHasMoreResults(false);
    setYear(defaultYear);
    setPage(0);
  };

  const handleYearChange = (event) => {
    const newYear = Number(event.target.value);

    if (!Number.isFinite(newYear)) {
      return;
    }

    pageTokensRef.current = { 0: null };
    setHasMoreResults(false);
    setYear(newYear);
    setPage(0);
  };

  const handleRowClick = (row) => {
    setSelectedRow(row);
  };

  const handleCloseDialog = () => {
    setSelectedRow(null);
  };

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <TextField
            select
            label="Year"
            value={year}
            onChange={handleYearChange}
            sx={{ minWidth: 160 }}
          >
            {yearOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={handleResetYear}
              disabled={year === defaultYear}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setPage(0);
                void loadEnrollments();
              }}
              startIcon={<RefreshIcon fontSize="small" />}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ position: "relative" }}>
        {loading && <LinearProgress />}

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && rows.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>
            No enrollments found for the selected year.
          </Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column}>{formatKeyToLabel(column)}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    key={index}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleRowClick(row)}
                  >
                    {columns.map((column) => (
                      <TableCell key={column}>
                        {formatValueForTable(row?.[column])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {(totalCount > 0 || hasMoreResults) && (
          <TablePagination
            component="div"
            count={hasMoreResults ? -1 : totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
      </Paper>

      <Dialog open={Boolean(selectedRow)} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Enrollment details</DialogTitle>
        <DialogContent dividers>
          {selectedRow ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {Object.entries(selectedRow).map(([key, value]) => (
                <Box key={key}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {formatKeyToLabel(key)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component={typeof value === "object" ? "pre" : "p"}
                    sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", m: 0 }}
                  >
                    {formatValueForDetails(value)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2">No enrollment selected.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default EnrollmentsManager;
